import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useCardRenewals } from "@/hooks/use-card-renewals";
import { formatCurrency } from "@/lib/utils";
import { CreditCard, AlertCircle, Wallet } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

export function PaymentCardOverview() {
  const { data, isLoading, isError } = useCardRenewals(30);

  if (isLoading) {
    return <PaymentCardSkeleton />;
  }

  if (isError || !data) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-medium flex items-center gap-2">
            <Wallet className="h-5 w-5 text-muted-foreground" />
            Payment Method
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <AlertCircle className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-1">Unable to load payment card data</p>
            <Button variant="outline" size="sm" asChild>
              <Link href="/payment-cards">Manage Payment Methods</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { defaultCard, renewals } = data;

  if (!defaultCard) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-medium flex items-center gap-2">
            <Wallet className="h-5 w-5 text-muted-foreground" />
            Payment Method
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <CreditCard className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-1">No default payment method set</p>
            <Button variant="outline" size="sm" asChild>
              <Link href="/payment-cards">Add Payment Method</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Format expiry date as MM/YY
  const expiryDate = `${defaultCard.expiryMonth.toString().padStart(2, '0')}/${defaultCard.expiryYear.toString().slice(-2)}`;
  
  // Last 4 digits with mask
  const maskedNumber = `•••• ${defaultCard.lastFourDigits}`;
  
  return (
    <Card className="h-full">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-medium flex items-center gap-2">
          <Wallet className="h-5 w-5 text-muted-foreground" />
          Payment Method
        </CardTitle>
        <CardDescription>Default payment method and upcoming charges</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col">
          <div className="flex items-start mb-4">
            <div className="mr-4">
              <CreditCard className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="font-medium">{defaultCard.cardName}</h3>
              <div className="flex items-center text-sm text-muted-foreground gap-2">
                <span>{defaultCard.cardProvider}</span>
                <span>•</span>
                <span>{maskedNumber}</span>
                <span>•</span>
                <span>Expires {expiryDate}</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-3 mt-3">
            <div className="flex justify-between items-center border-b pb-2">
              <span className="text-sm font-medium">Today's renewals</span>
              <span className="font-medium">
                {renewals.todayRenewals.length > 0 
                  ? formatCurrency(renewals.todayRenewalsCost)
                  : <span className="text-muted-foreground">None</span>
                }
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Next 30 days</span>
              <span className="font-medium">
                {renewals.licenses.length > 0
                  ? formatCurrency(renewals.totalCost)
                  : <span className="text-muted-foreground">No renewals</span>
                }
              </span>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-1">
        <Button variant="outline" size="sm" className="w-full" asChild>
          <Link href="/payment-cards">
            Manage Payment Methods
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}

function PaymentCardSkeleton() {
  return (
    <Card className="h-full">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-medium">
          <Skeleton className="h-6 w-48" />
        </CardTitle>
        <CardDescription>
          <Skeleton className="h-4 w-64" />
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start mb-4">
          <Skeleton className="h-8 w-8 mr-4 rounded-md" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-full" />
          </div>
        </div>
        <div className="space-y-4 mt-4">
          <div className="flex justify-between items-center border-b pb-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-16" />
          </div>
          <div className="flex justify-between items-center">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-1">
        <Skeleton className="h-9 w-full" />
      </CardFooter>
    </Card>
  );
}